"use client";

import { useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseLines, parseTermLines } from "@/lib/tool-kit/classroom/entries";
import {
  buildMatchingSheet,
  MAX_MATCHING_RESPONSES,
  type MatchingSheet,
} from "@/lib/tool-kit/classroom/matching";
import { secureRandom } from "@/lib/tool-kit/classroom/random";

const MIN_PAIRS = 2;
const SHEET_H2 = "font-semibold text-xl";

/** English strings. A translated content file overrides these through `ui`. */
export const MATCHING_WORKSHEET_GENERATOR_STRINGS = {
  pairsLabel: "Terms and definitions — one pair per line, term first",
  pairsHint:
    "Separate each term from its definition with a dash, colon or tab.",
  pairsPlaceholder:
    "delta - Landform built from sediment where a river meets the sea\ntributary - A smaller stream that flows into a larger river",
  example:
    "delta - Landform built from sediment where a river meets the sea\ntributary - A smaller stream that flows into a larger river\nmeander - A wide, looping bend in a river\nestuary - Where fresh river water mixes with salt water from the sea\nwatershed - All the land that drains into one river system\nsource - The place where a river begins\nmouth - The place where a river flows into the sea or a lake\nfloodplain - Flat land beside a river that floods when water levels rise",
  exampleDistractors:
    "A lake left behind when a meander is cut off\nA steep drop where a river flows over hard rock",
  loadExample: "Load example",
  pairCount: "one:{count} pair|other:{count} pairs",
  distractorsLabel: "Extra answer choices (optional) — one per line",
  distractorsHint:
    "Distractors match nothing, so the last answers cannot be found by elimination.",
  distractorsPlaceholder: "A lake left behind when a meander is cut off",
  titleLabel: "Worksheet title",
  titlePlaceholder: "Rivers: Key Vocabulary",
  defaultTitle: "Matching",
  instructionsLabel: "Instructions",
  defaultInstructions:
    "Write the letter of the matching definition on the line beside each term.",
  defaultInstructionsSwapped:
    "Write the letter of the matching term on the line beside each definition.",
  unusedNote: "Some answer choices will not be used.",
  swap: "Number the definitions and letter the terms",
  showNameLine: "Name and date line",
  options: "Options",
  generate: "Create worksheet",
  reshuffle: "Reshuffle",
  printSheet: "Print worksheet",
  printKey: "Print answer key",
  showKey: "Show answer key",
  hideKey: "Hide answer key",
  copyText: "Copy worksheet as text",
  copied: "Copied!",
  answerKeyTitle: "{title} — Answer key",
  name: "Name",
  date: "Date",
  needPairs: "Enter at least {min} pairs.",
  tooMany:
    "Use at most {max} answer choices in all (pairs plus extras), so they can be lettered A–Z.",
  invalidLines:
    "Lines without a separator were skipped: {lines}. Write each line as term - definition.",
} as const;

type Strings = typeof MATCHING_WORKSHEET_GENERATOR_STRINGS;
type PrintTarget = "sheet" | "key";

function instructionsFor(
  custom: string,
  swap: boolean,
  sheet: MatchingSheet,
  strings: Strings
): string {
  const base =
    custom.trim() ||
    (swap ? strings.defaultInstructionsSwapped : strings.defaultInstructions);
  return sheet.hasDistractors ? `${base} ${strings.unusedNote}` : base;
}

function worksheetText(
  title: string,
  instructions: string,
  sheet: MatchingSheet,
  strings: Strings,
  nameLine: boolean
): string {
  return [
    title,
    nameLine ? `${strings.name}: ____________   ${strings.date}: ________` : "",
    instructions,
    "",
    ...sheet.items.map((item) => `____ ${item.number}. ${item.text}`),
    "",
    ...sheet.responses.map(
      (response) => `${response.letter}. ${response.text}`
    ),
  ]
    .filter((line, index) => !(index === 1 && line === ""))
    .join("\n");
}

function Worksheet({
  sheet,
  title,
  instructions,
  nameLine,
  strings,
  className,
}: {
  sheet: MatchingSheet;
  title: string;
  instructions: string;
  nameLine: boolean;
  strings: Strings;
  className: string;
}) {
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>{title}</h2>
      {nameLine ? (
        <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
          <span className="flex items-end gap-2">
            {strings.name}
            <span className="inline-block w-56 border-current border-b" />
          </span>
          <span className="flex items-end gap-2">
            {strings.date}
            <span className="inline-block w-32 border-current border-b" />
          </span>
        </div>
      ) : null}
      <p className="text-sm italic">{instructions}</p>
      <div className="grid gap-8 sm:grid-cols-2 print:grid-cols-2">
        <ol className="space-y-4 text-sm">
          {sheet.items.map((item) => (
            <li className="flex items-end gap-2" key={item.number}>
              <span className="inline-block w-10 shrink-0 border-current border-b" />
              <span className="w-6 shrink-0 text-right font-semibold">
                {item.number}.
              </span>
              <span>{item.text}</span>
            </li>
          ))}
        </ol>
        <ul className="space-y-4 text-sm">
          {sheet.responses.map((response) => (
            <li className="flex gap-2" key={response.letter}>
              <span className="w-6 shrink-0 font-semibold">
                {response.letter}.
              </span>
              <span>{response.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function AnswerKey({
  sheet,
  title,
  strings,
  className,
}: {
  sheet: MatchingSheet;
  title: string;
  strings: Strings;
  className: string;
}) {
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>{t(strings.answerKeyTitle, { title })}</h2>
      <ol className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2 print:grid-cols-2">
        {sheet.items.map((item) => (
          <li className="flex gap-2" key={item.number}>
            <span className="w-6 shrink-0 text-right font-semibold">
              {item.number}.
            </span>
            <span className="w-6 shrink-0 font-semibold">{item.answer}</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function MatchingWorksheetGeneratorWidget() {
  const strings = useToolUi(MATCHING_WORKSHEET_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [distractorInput, setDistractorInput] = useState("");
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [swap, setSwap] = useState(false);
  const [nameLine, setNameLine] = useState(true);
  const [sheet, setSheet] = useState<MatchingSheet | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("sheet");
  const [error, setError] = useState("");

  const parsed = parseTermLines(input);
  const distractors = parseLines(distractorInput);
  const heading = title.trim() || strings.defaultTitle;

  function build(nextSwap: boolean) {
    if (parsed.entries.length < MIN_PAIRS) {
      setError(t(strings.needPairs, { min: MIN_PAIRS }));
      setSheet(null);
      return;
    }
    if (parsed.entries.length + distractors.length > MAX_MATCHING_RESPONSES) {
      setError(t(strings.tooMany, { max: MAX_MATCHING_RESPONSES }));
      setSheet(null);
      return;
    }
    setError("");
    setSheet(
      buildMatchingSheet(
        parsed.entries.map((entry) => ({
          term: entry.term,
          definition: entry.clue,
        })),
        distractors,
        { swap: nextSwap, random: secureRandom }
      )
    );
  }

  function handleSwap(value: boolean) {
    setSwap(value);
    if (sheet) {
      build(value);
    }
  }

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  function loadExample() {
    setInput(strings.example);
    setDistractorInput(strings.exampleDistractors);
  }

  const shownInstructions = sheet
    ? instructionsFor(instructions, swap, sheet, strings)
    : "";
  const sheetClass = "space-y-6 text-foreground print:text-black";

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <TextAreaField
          hint={
            <>
              {strings.pairsHint}{" "}
              {tPlural(strings.pairCount, locale, parsed.entries.length, {
                count: parsed.entries.length,
              })}
            </>
          }
          id="mw-pairs"
          label={strings.pairsLabel}
          onChange={setInput}
          placeholder={strings.pairsPlaceholder}
          prose
          value={input}
        />
        <button className={BUTTON_SMALL} onClick={loadExample} type="button">
          {strings.loadExample}
        </button>
        <TextAreaField
          hint={strings.distractorsHint}
          id="mw-distractors"
          label={strings.distractorsLabel}
          onChange={setDistractorInput}
          placeholder={strings.distractorsPlaceholder}
          prose
          rows={3}
          value={distractorInput}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="mw-title"
            label={strings.titleLabel}
            onChange={setTitle}
            placeholder={strings.titlePlaceholder}
            value={title}
          />
          <TextField
            id="mw-instructions"
            label={strings.instructionsLabel}
            onChange={setInstructions}
            placeholder={
              swap
                ? strings.defaultInstructionsSwapped
                : strings.defaultInstructions
            }
            value={instructions}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className="font-medium text-foreground text-sm">
            {strings.options}
          </legend>
          <Checkbox checked={swap} label={strings.swap} onChange={handleSwap} />
          <Checkbox
            checked={nameLine}
            label={strings.showNameLine}
            onChange={setNameLine}
          />
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={parsed.entries.length === 0}
            onClick={() => build(swap)}
            type="button"
          >
            {sheet ? strings.reshuffle : strings.generate}
          </button>
          {sheet ? (
            <>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("sheet")}
                type="button"
              >
                {strings.printSheet}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("key")}
                type="button"
              >
                {strings.printKey}
              </button>
              <button
                aria-pressed={showKey}
                className={BUTTON_SECONDARY}
                onClick={() => setShowKey((value) => !value)}
                type="button"
              >
                {showKey ? strings.hideKey : strings.showKey}
              </button>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyText}
                text={worksheetText(
                  heading,
                  shownInstructions,
                  sheet,
                  strings,
                  nameLine
                )}
              />
            </>
          ) : null}
        </div>
      </div>

      <div aria-live="polite" className="space-y-3 print:hidden">
        {error ? <ErrorNotice>{error}</ErrorNotice> : null}
        {parsed.invalidLines.length > 0 ? (
          <Notice>
            {t(strings.invalidLines, {
              lines: parsed.invalidLines.join(", "),
            })}
          </Notice>
        ) : null}
      </div>

      {sheet ? (
        <Worksheet
          className={`${sheetClass} ${printTarget === "key" ? "print:hidden" : ""}`}
          instructions={shownInstructions}
          nameLine={nameLine}
          sheet={sheet}
          strings={strings}
          title={heading}
        />
      ) : null}
      {sheet ? (
        <AnswerKey
          className={`${sheetClass} ${showKey ? "" : "hidden"} ${printTarget === "key" ? "print:block" : "print:hidden"}`}
          sheet={sheet}
          strings={strings}
          title={heading}
        />
      ) : null}
    </div>
  );
}
